import { useEffect, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import AlertBox from "../../../../Components/AlertComp/AlertBox";

const DiamondMaster = () => {
  const url = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const axiosConfig = {
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
  };

  const [rows, setRows] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [addTableOpen, setAddTableOpen] = useState(false);
  const [newTableName, setNewTableName] = useState("");
  const [newTableMode, setNewTableMode] = useState("quality");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success | error
  const [fetchLoading, setFetchLoading] = useState(false);

  useEffect(() => {
    fetchDiamondRates();
  }, []);

  const fetchDiamondRates = async () => {
    setFetchLoading(true);
    try {
      const res = await axios.get(`${url}/diamond`, axiosConfig);

      // 🔐 SAFETY CHECK
      const data = Array.isArray(res.data) ? res.data : res.data.data;

      const normalizedRows = (data || []).map((row) => ({
        ...row,
        priceMode: row.priceMode || "quality",
        singlePrice: row.singlePrice ?? "",
        qualityRates: row.qualityRates || {
          lab_vvs_vs: row.lab_vvs_vs ?? "",
          natural_fg_vs: row.natural_fg_vs ?? "",
          natural_gh_si: row.natural_gh_si ?? "",
          natural_hi_si: row.natural_hi_si ?? "",
        },
      }));

      setRows(normalizedRows);
    } catch (error) {
      console.error("Failed to fetch diamond rates", error);
      setRows([]);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row._id === id ? { ...row, [field]: value } : row)),
    );
  };

  const handleQualityChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row._id === id
          ? {
              ...row,
              qualityRates: {
                ...row.qualityRates,
                [field]: value,
              },
            }
          : row,
      ),
    );
  };

  const saveRow = async (row) => {
    setLoading(true);
    try {
      const priceMode = row.priceMode || "quality";
      const payload = {
        shape: row.shape,
        mm: Number(row.mm),
        caratWeight: Number(row.caratWeight),
        sieveSize: row.sieveSize,
        priceMode,
        singlePrice: Number(row.singlePrice || 0),
        lab_vvs_vs:
          priceMode === "quality"
            ? Number(row.qualityRates.lab_vvs_vs)
            : 0,
        natural_fg_vs:
          priceMode === "quality"
            ? Number(row.qualityRates.natural_fg_vs)
            : 0,
        natural_gh_si:
          priceMode === "quality"
            ? Number(row.qualityRates.natural_gh_si)
            : 0,
        natural_hi_si:
          priceMode === "quality"
            ? Number(row.qualityRates.natural_hi_si)
            : 0,
      };

      if (!row.isNew) payload._id = row._id;

      if (row.isNew) {
        await axios.post(`${url}/diamond`, payload, axiosConfig);
      } else {
        await axios.put(`${url}/diamond/${row._id}`, payload, axiosConfig);
      }

      setMessage("Diamond slab saved successfully");
      setMessageType("success");

      fetchDiamondRates();
    } catch (err) {
      setMessage("Failed to save diamond slab");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async () => {
    setLoading(true);
    try {
      const res = await axios.patch(`${url}/diamond/all`, rows, axiosConfig);
      if (res.data.type === "success") {
        setMessage(res.data.message);
        setMessageType("success");
      }

      fetchDiamondRates();
    } catch (err) {
      console.log("err ===> ", err);
      setMessage("Failed to save diamond slab");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${url}/diamond/${id}`, axiosConfig);

      setMessage("Diamond slab deleted successfully");
      setMessageType("success");

      fetchDiamondRates();
    } catch (err) {
      setMessage("Failed to delete diamond slab");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const addRow = (shapeOverride, priceModeOverride) => {
    const shapeValue = shapeOverride || "";
    const modeValue = priceModeOverride || "quality";
    setRows((prev) => [
      ...prev,
      {
        _id: `tmp-${Date.now()}`,
        shape: shapeValue,
        mm: "",
        caratWeight: "",
        sieveSize: "",
        priceMode: modeValue,
        singlePrice: "",
        qualityRates: {
          lab_vvs_vs: "100",
          natural_fg_vs: "200",
          natural_gh_si: "250",
          natural_hi_si: "200",
        },
        isNew: true,
      },
    ]);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setMessage("");
      setMessageType("");
      let alertBox = document?.getElementById("alert-box");
      alertBox?.classList?.remove("alert-wrapper");
    }, 1500);

    return () => clearTimeout(timer);
  }, [messageType, message]);

  const tables = Array.from(
    rows.reduce((map, row) => {
      const shape = (row.shape || "").trim();
      if (!shape) return map;
      if (!map.has(shape)) {
        map.set(shape, row.priceMode || "quality");
      }
      return map;
    }, new Map()),
    ([shape, priceMode]) => ({ shape, priceMode }),
  );

  const activeTable = tables[activeTab] || null;
  const activeShape = activeTable?.shape || "";
  const activeMode = activeTable?.priceMode || "quality";

  const visibleRows = activeShape
    ? rows.filter(
        (row) => (row.shape || "").trim().toLowerCase() === activeShape.toLowerCase(),
      )
    : rows;

  useEffect(() => {
    if (tables.length === 0) {
      setActiveTab(0);
      return;
    }
    if (activeTab >= tables.length) {
      setActiveTab(tables.length - 1);
    }
  }, [tables.length, activeTab]);

  return (
    <div className="main-content">
      <AlertBox status={messageType} statusMessage={message} />

      <div className="page-content">
        <div className="container-fluid">
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={"center"}
            mb={3}
          >
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Diamond Master
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Editable pricing slabss
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => setAddTableOpen(true)}
                variant="outlined"
                sx={{
                  borderColor: "#D4AF37",
                  color: "#8A6D1D",
                  borderRadius: "8px",
                  "&:hover": { borderColor: "#B8962E", color: "#6f5520" },
                }}
              >
                Add Table
              </Button>
              <Button
                onClick={saveAll}
                variant="contained"
                sx={{
                  bgcolor: "#D4AF37",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#B8962E" },
                }}
              >
                Save All Slab
              </Button>
            </Stack>
          </Stack>

          <Paper sx={{ p: 3, borderRadius: "12px" }}>
            {tables.length > 0 ? (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Tabs
                    value={activeTab}
                    onChange={(e, val) => setActiveTab(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {tables.map((t) => (
                      <Tab
                        key={t.shape}
                        label={`${t.shape} (${t.priceMode === "single" ? "Single Price" : "Qualities"})`}
                      />
                    ))}
                  </Tabs>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={!activeShape}
                    onClick={async () => {
                      if (!activeShape) return;
                      if (!window.confirm(`Delete entire "${activeShape}" table?`)) return;

                      const targetRows = rows.filter(
                        (row) =>
                          (row.shape || "").trim().toLowerCase() ===
                          activeShape.toLowerCase(),
                      );

                      try {
                        setLoading(true);
                        await Promise.all(
                          targetRows
                            .filter((r) => r._id && !String(r._id).startsWith("tmp-"))
                            .map((r) => axios.delete(`${url}/diamond/${r._id}`, axiosConfig)),
                        );
                        setRows((prev) =>
                          prev.filter(
                            (row) =>
                              (row.shape || "").trim().toLowerCase() !==
                              activeShape.toLowerCase(),
                          ),
                        );
                        setMessage("Table deleted successfully");
                        setMessageType("success");
                      } catch (err) {
                        setMessage("Failed to delete table");
                        setMessageType("error");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    sx={{ ml: 2, borderRadius: "8px" }}
                  >
                    Delete Table
                  </Button>
                </Stack>
                <Typography variant="caption" color="textSecondary">
                  Pricing Mode:{" "}
                  {activeMode === "single" ? "Single price per carat" : "Quality-based pricing"}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                No pricing tables yet. Click “Add Table” to create one.
              </Typography>
            )}

            {/* Header Row */}
            <Stack
              direction="row"
              spacing={2}
              sx={{
                mb: 2,
                fontWeight: 600,
                color: "#777",
                fontSize: "12px",
              }}
            >
              <Box width={100}>Shape</Box>
              <Box width={100}>MM Size</Box>
              <Box width={100}>Ct. Weight/Peice</Box>
              <Box width={100}>Sieve Size</Box>
              {activeMode === "single" ? (
                <Box width={140}>Price / Carat</Box>
              ) : (
                <>
                  <Box width={120}>LAB VVS/VS</Box>
                  <Box width={120}>FG VS</Box>
                  <Box width={120}>GH SI</Box>
                  <Box width={120}>HI SI</Box>
                </>
              )}
              <Box width={100}>Action</Box>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {fetchLoading ? (
              <div className="p-5 m-5 d-flex align-items-center justify-content-center"><CircularProgress size={80} /></div>
            ) : (
              <>
                {visibleRows.map((row, index) => (
                  <Box key={row._id} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        size="small"
                        value={row.shape}
                        onChange={(e) =>
                          handleChange(row._id, "shape", e.target.value)
                        }
                        sx={{ width: 100 }}
                      />
                      <TextField
                        size="small"
                        value={row.mm}
                        onChange={(e) =>
                          handleChange(row._id, "mm", e.target.value)
                        }
                        sx={{ width: 100 }}
                      />
                      <TextField
                        size="small"
                        value={row.caratWeight}
                        onChange={(e) =>
                          handleChange(
                            row._id,
                            "caratWeight",
                            e.target.value,
                          )
                        }
                        sx={{ width: 100 }}
                      />

                      <TextField
                        size="small"
                        value={row.sieveSize}
                        onChange={(e) =>
                          handleChange(row._id, "sieveSize", e.target.value)
                        }
                        sx={{ width: 100 }}
                      />

                      {activeMode === "single" ? (
                        <TextField
                          size="small"
                          value={row.singlePrice ?? ""}
                          onChange={(e) =>
                            handleChange(
                              row._id,
                              "singlePrice",
                              e.target.value,
                            )
                          }
                          sx={{ width: 140 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }}
                        />
                      ) : (
                        [
                          "lab_vvs_vs",
                          "natural_fg_vs",
                          "natural_gh_si",
                          "natural_hi_si",
                        ].map((q) => (
                          <TextField
                            key={q}
                            size="small"
                            value={row.qualityRates[q] ?? ""}
                            onChange={(e) =>
                              handleQualityChange(row._id, q, e.target.value)
                            }
                            sx={{ width: 120 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                            }}
                          />
                        ))
                      )}

                      <IconButton
                        disabled={loading}
                        onClick={() => saveRow(row)}
                        // sx={{
                        //   backgroundColor: "#000",
                        // }}
                      >
                        {loading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <SaveIcon sx={{ color: "#1a1a1a" }} />
                        )}
                      </IconButton>

                      {!row.isNew && (
                        <IconButton
                          disabled={loading}
                          onClick={() => deleteRow(row._id)}
                          // sx={{
                          //   backgroundColor: "#d32f2f",
                          // }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>

                    {index !== visibleRows.length - 1 && (
                      <Divider sx={{ mt: 2 }} />
                    )}
                  </Box>
                ))}

                <Button
                  onClick={() => addRow(activeShape, activeMode)}
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={!activeShape}
                  sx={{
                    bgcolor: "#D4AF37",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#B8962E" },
                  }}
                >
                  Add Slab
                </Button>
              </>
            )}
          </Paper>

          <Dialog open={addTableOpen} onClose={() => setAddTableOpen(false)}>
            <DialogTitle>Add Pricing Table</DialogTitle>
            <DialogContent sx={{ minWidth: 380 }}>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Diamond Type / Shape"
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  placeholder="Round, Ruby, Emerald..."
                  size="small"
                />
                <FormControl>
                  <FormLabel>Pricing Type</FormLabel>
                  <RadioGroup
                    row
                    value={newTableMode}
                    onChange={(e) => setNewTableMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="quality"
                      control={<Radio />}
                      label="Qualities"
                    />
                    <FormControlLabel
                      value="single"
                      control={<Radio />}
                      label="Single Price"
                    />
                  </RadioGroup>
                </FormControl>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddTableOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#D4AF37",
                  "&:hover": { bgcolor: "#B8962E" },
                }}
                onClick={() => {
                  const shape = newTableName.trim();
                  if (!shape) return;

                  const existingIndex = tables.findIndex(
                    (t) => t.shape.toLowerCase() === shape.toLowerCase(),
                  );

                  if (existingIndex >= 0) {
                    setActiveTab(existingIndex);
                    setAddTableOpen(false);
                    return;
                  }

                  addRow(shape, newTableMode);
                  setAddTableOpen(false);
                  setNewTableName("");
                  setNewTableMode("quality");
                  setActiveTab(tables.length);
                }}
              >
                Create Table
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default DiamondMaster;
